import React, { useState } from 'react';
import { useCookieConsent, CookiePreferences } from '../contexts/CookieConsentContext';

interface CookieSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CookieSettingsModal: React.FC<CookieSettingsModalProps> = ({ isOpen, onClose }) => {
  const { preferences, updatePreferences } = useCookieConsent();
  const [tempPreferences, setTempPreferences] = useState<CookiePreferences>(preferences);

  React.useEffect(() => {
    setTempPreferences(preferences);
  }, [preferences]);

  if (!isOpen) {return null;}

  const handleSave = () => {
    updatePreferences(tempPreferences);
    onClose();
  };

  const handleCancel = () => {
    setTempPreferences(preferences);
    onClose();
  };

  const handlePreferenceChange = (key: keyof CookiePreferences, value: boolean) => {
    if (key === 'essential') {return;} // Essential cookies cannot be disabled
    setTempPreferences(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Cookie Preferences</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          <div className="space-y-6">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-green-800">Essential Cookies</h3>
                  <p className="text-sm text-green-700 mt-1">
                    These cookies are necessary for the website to function and cannot be switched off.
                  </p>
                  <p className="text-xs text-green-600 mt-2">
                    Includes: Wallet connection state, session management, security features
                  </p>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled={true}
                    className="w-4 h-4 text-green-600 bg-green-100 border-green-300 rounded focus:ring-green-500"
                  />
                </div>
              </div>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-blue-800">Analytics Cookies</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    These cookies help us understand how visitors interact with our website.
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    Includes: Google Analytics, Vercel Analytics (page views, session duration, user behavior insights)
                  </p>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={tempPreferences.analytics}
                    onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-blue-100 border-blue-300 rounded focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-orange-800">Essential Services</h3>
                  <p className="text-sm text-orange-700 mt-1">
                    These services are required for the core functionality of the application.
                  </p>
                  <p className="text-xs text-orange-600 mt-2">
                    Includes: Solana RPC endpoints, decentralized storage providers (Lighthouse with Pinata/Arweave fallbacks), wallet providers
                  </p>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled={true}
                    className="w-4 h-4 text-orange-600 bg-orange-100 border-orange-300 rounded focus:ring-orange-500"
                  />
                </div>
              </div>
            </div>

            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-purple-800">Optional Services</h3>
                  <p className="text-sm text-purple-700 mt-1">
                    These services enhance the user experience but are not required for core functionality.
                  </p>
                  <p className="text-xs text-purple-600 mt-2">
                    Includes: Google Fonts (custom typography)
                  </p>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={tempPreferences.optionalServices}
                    onChange={(e) => handlePreferenceChange('optionalServices', e.target.checked)}
                    className="w-4 h-4 text-purple-600 bg-purple-100 border-purple-300 rounded focus:ring-purple-500"
                  />
                </div>
              </div>
            </div>

          </div>

          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-800 mb-2">Your Rights</h4>
            <p className="text-sm text-gray-600 mb-3">
              You have the right to withdraw your consent at any time. You can also request information about 
              the data we process, object to processing, or request deletion of your data. 
              Contact us at <a href="mailto:main@soulminter.io" className="text-blue-600 hover:underline">main@soulminter.io</a> for more information.
            </p>
            <p className="text-xs text-gray-500">
              <strong>Note:</strong> Disabling analytics cookies will prevent Google Analytics and Vercel Analytics from collecting data about your usage of this website.
            </p>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Save Preferences
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieSettingsModal;
